import React, { useState, useEffect } from 'react';
import { useTheme } from './ThemeProvider';
import { useUserData } from './UserDataContext';
import { ReportService, UserService } from '../services/api';
import { Report } from '../types';
import { 
    Shield, CheckCircle, XCircle, AlertTriangle, 
    ChevronRight, ExternalLink, Clock, User, Ban, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { formatTimeAgo } from './utils/timeFormat';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'motion/react';
import { useScreenReady } from '../contexts/ScreenTransitionContext';
import { BanAppealService, type BanAppeal } from '../services/BanAppealService';

interface WatcherDashboardProps {
    onBack: () => void;
}

export function WatcherDashboard({ onBack }: WatcherDashboardProps) {
    const { theme } = useTheme();
    const { userProfile } = useUserData();
    const [reports, setReports] = useState<Report[]>([]);
    const [loading, setLoading] = useState(true);
    useScreenReady(!loading);
    const [selectedReport, setSelectedReport] = useState<Report | null>(null);
    const [processing, setProcessing] = useState(false);
    const [resolutionNote, setResolutionNote] = useState('');

    // Appeals ride in this same queue as another tab rather than a separate
    // screen — a Watcher works one list, not two.
    const [tab, setTab] = useState<'reports' | 'appeals'>('reports');
    const [appeals, setAppeals] = useState<Array<BanAppeal & { id: string }>>([]);
    const [appealBusy, setAppealBusy] = useState<string | null>(null);

    useEffect(() => {
        loadReports();
        loadAppeals();
    }, []);

    const loadAppeals = async () => {
        setAppeals(await BanAppealService.fetchAppeals('pending'));
    };

    const decideAppeal = async (userId: string, approve: boolean) => {
        const note = window.prompt(
            approve
                ? 'Optional note (shown in the audit log):'
                : 'Why is this appeal denied? The user sees this.',
        );
        if (note === null) return;
        setAppealBusy(userId);
        const watcherId = String(userProfile?.id || '');
        const res = approve
            ? await BanAppealService.approveAppeal(userId, watcherId, note.trim() || undefined)
            : await BanAppealService.denyAppeal(userId, watcherId, note.trim() || undefined);
        if (res.success) {
            // Approving lifts the ban; because the client watches its own profile
            // document live, that account is back in the app without relaunching.
            toast.success(approve ? 'Appeal approved — ban lifted' : 'Appeal denied');
            setAppeals(prev => prev.filter(a => a.id !== userId));
        } else {
            toast.error(res.error || 'Could not record that decision');
        }
        setAppealBusy(null);
    };

    const loadReports = async () => {
        setLoading(true);
        const result = await ReportService.fetchPendingReports();
        if (result.success && result.reports) {
            setReports(result.reports);
        } else {
            toast.error('Failed to load reports');
        }
        setLoading(false);
    };

    const handleResolve = async (status: 'resolved' | 'dismissed') => {
        if (!selectedReport) return;
        setProcessing(true);
        
        // If resolving (banning/action taken), require a note
        if (status === 'resolved' && !resolutionNote.trim()) {
            toast.error('Please add a resolution note');
            setProcessing(false);
            return;
        }

        const result = await ReportService.updateReportStatus(
            selectedReport.id, 
            status, 
            resolutionNote
        );

        if (result.success) {
            toast.success(`Report ${status}`);
            setReports(prev => prev.filter(r => r.id !== selectedReport.id));
            setSelectedReport(null);
            setResolutionNote('');
        } else {
            toast.error(`Failed to ${status} report`);
        }
        setProcessing(false);
    };

    // Verify Rank
    if (userProfile?.rank !== 'watcher' && userProfile?.rank !== 'legion') {
        return (
            <div className="flex items-center justify-center h-screen bg-[#eddeb7] dark:bg-[#0D0D0D]">
                <div className="text-center p-6 bg-red-100 dark:bg-red-900/20 rounded-xl border border-red-500 max-w-sm mx-auto">
                    <Ban className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h1 className="text-xl font-bold text-red-500">Access Denied</h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-2">
                        You do not have permission to view this dashboard.
                    </p>
                    <Button onClick={onBack} className="mt-4 w-full">Go Back</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-screen bg-[#eddeb7] dark:bg-[#0D0D0D] flex justify-center">
            <div className="w-full max-w-2xl h-full flex flex-col bg-white/20 dark:bg-black/20 shadow-2xl border-x border-black/5 dark:border-white/5 relative overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-black/10 dark:border-white/10 flex items-center justify-between bg-[#F5F5DC]/80 dark:bg-[#121212] backdrop-blur-md"
                    style={{ paddingTop: 'max(16px, calc(env(safe-area-inset-top) + 16px))' }}>
                    <div className="flex items-center gap-3">
                        <motion.button 
                            whileTap={{ scale: 0.9 }}
                            onClick={onBack}
                            className="p-2 -ml-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                        >
                            <ArrowLeft className="w-6 h-6 text-black dark:text-white" />
                        </motion.button>
                        <Shield className="w-6 h-6 text-[#F39C12] dark:text-[#4478ea]" />
                        <h1 className="text-xl font-bold text-black dark:text-white line-clamp-1">Watcher Dashboard</h1>
                    </div>
                    <Button variant="ghost" onClick={loadReports} size="sm" className="text-black dark:text-white hover:bg-black/5 dark:hover:bg-white/10">
                        Refresh
                    </Button>
                </div>

            {/* minHeight: 0 on the intermediate wrappers too, not just the
                ScrollArea. Each of these is itself a flex child with the default
                min-height:auto, so they grew with the report list and the scroll
                area never overflowed. */}
            <div className="flex-1 overflow-hidden flex flex-col md:flex-row" style={{ minHeight: 0 }}>
                {/* Reports List */}
                <div className={`flex-1 md:flex-none md:w-96 flex flex-col border-r border-black/10 dark:border-white/10 ${selectedReport ? 'hidden md:flex' : 'flex'}`} style={{ minHeight: 0 }}>
                    <div className="flex bg-black/5 dark:bg-white/5">
                        <button
                            onClick={() => setTab('reports')}
                            className={`flex-1 p-4 font-medium text-sm ${tab === 'reports' ? 'text-black dark:text-white border-b-2 border-[#F39C12] dark:border-[#4478ea]' : 'text-gray-500'}`}
                        >
                            REPORTS ({reports.length})
                        </button>
                        <button
                            onClick={() => setTab('appeals')}
                            className={`flex-1 p-4 font-medium text-sm ${tab === 'appeals' ? 'text-black dark:text-white border-b-2 border-[#F39C12] dark:border-[#4478ea]' : 'text-gray-500'}`}
                        >
                            APPEALS ({appeals.length})
                        </button>
                    </div>
                    <ScrollArea className="flex-1" style={{ minHeight: 0 }}>
                        {tab === 'appeals' ? (
                            appeals.length === 0 ? (
                                <div className="p-8 text-center text-gray-500 flex flex-col items-center gap-2">
                                    <CheckCircle className="w-8 h-8 text-green-500" />
                                    <p>No appeals waiting.</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-black/10 dark:divide-white/10">
                                    {appeals.map(a => (
                                        <div key={a.id} className="p-4">
                                            <div className="flex justify-between items-start mb-2">
                                                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">
                                                    APPEAL
                                                </span>
                                                <span className="text-[11px] text-gray-400">{a.id.slice(0, 10)}</span>
                                            </div>
                                            {a.banReason ? (
                                                <p className="text-[11px] text-gray-500 mb-1">
                                                    Banned for: {a.banReason}
                                                </p>
                                            ) : null}
                                            <p className="text-sm text-black dark:text-white break-words whitespace-pre-wrap">
                                                {a.appealText}
                                            </p>
                                            <div className="flex gap-2 mt-3">
                                                <button
                                                    onClick={() => decideAppeal(a.id, true)}
                                                    disabled={appealBusy === a.id}
                                                    className="flex-1 py-2 rounded-lg text-xs font-bold text-white bg-green-600 disabled:opacity-50"
                                                >
                                                    Approve — lift ban
                                                </button>
                                                <button
                                                    onClick={() => decideAppeal(a.id, false)}
                                                    disabled={appealBusy === a.id}
                                                    className="flex-1 py-2 rounded-lg text-xs font-bold text-white bg-[#E74C3C] disabled:opacity-50"
                                                >
                                                    Deny
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )
                        ) : loading ? (
                            <div className="p-8 text-center text-gray-500">Loading...</div>
                        ) : reports.length === 0 ? (
                            <div className="p-8 text-center text-gray-500 flex flex-col items-center gap-2">
                                <CheckCircle className="w-8 h-8 text-green-500" />
                                <p>All caught up! No pending reports.</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-black/10 dark:divide-white/10">
                                {reports.map(report => (
                                    <div 
                                        key={report.id}
                                        onClick={() => setSelectedReport(report)}
                                        className={`p-4 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 transition-colors ${selectedReport?.id === report.id ? 'bg-[#F39C12]/10 dark:bg-[#4478ea]/10 border-l-4 border-[#F39C12] dark:border-[#4478ea]' : ''}`}
                                    >
                                        <div className="flex justify-between items-start mb-1">
                                            <span className="flex items-center gap-1.5">
                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                                    report.targetType === 'user' ? 'bg-purple-100 text-purple-600' :
                                                    report.targetType === 'yap' ? 'bg-blue-100 text-blue-600' :
                                                    'bg-gray-100 text-gray-600'
                                                }`}>
                                                    {report.targetType?.toUpperCase() || 'UNKNOWN'}
                                                </span>
                                                {/* A system-filed flag reads differently from a member
                                                    report: nobody complained, a keyword matched. Same
                                                    report type and same review step — the badge only
                                                    says where it came from, so a Watcher weighs it
                                                    knowing a substring match can be a false positive. */}
                                                {(report as any).flaggedBy === 'auto' && (
                                                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">
                                                        AUTO
                                                    </span>
                                                )}
                                            </span>
                                            <span className="text-xs text-gray-400">
                                                {formatTimeAgo(report.timestamp?.toDate ? report.timestamp.toDate() : new Date())}
                                            </span>
                                        </div>
                                        <h3 className="font-semibold text-black dark:text-white mb-1">
                                            {report.category}
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                                            {report.reason}
                                        </p>
                                        <div className="mt-2 text-xs text-gray-400 flex items-center gap-1">
                                            <AlertTriangle className="w-3 h-3" />
                                            Reported by @{report.reporterUsername?.replace(/^@/, '') || 'unknown'}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </ScrollArea>
                </div>

                {/* Report Detail View */}
                <div className={`flex-1 flex flex-col bg-white/50 dark:bg-black/50 ${!selectedReport ? 'hidden md:flex' : 'flex'}`}>
                    {selectedReport ? (
                        <>
                            <div className="p-4 border-b border-black/10 dark:border-white/10 flex items-center gap-3">
                                <Button variant="ghost" className="md:hidden" onClick={() => setSelectedReport(null)}>
                                    Back
                                </Button>
                                <h2 className="font-bold text-lg text-black dark:text-white">Review Report</h2>
                            </div>
                            
                            <ScrollArea className="flex-1 p-4 md:p-8">
                                <div className="max-w-2xl mx-auto space-y-6">
                                    {/* Reporter Info */}
                                    <div className="bg-white dark:bg-[#1a1a1a] p-4 rounded-xl shadow-sm">
                                        <h3 className="text-sm font-bold text-gray-500 mb-3 uppercase tracking-wider">Reporter</h3>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                                <User className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <p className="font-bold text-black dark:text-white">@{selectedReport.reporterUsername?.replace(/^@/, '') || 'unknown'}</p>
                                                <p className="text-xs text-gray-500">ID: {selectedReport.reporterId}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Reported Target Info */}
                                    <div className="bg-white dark:bg-[#1a1a1a] p-4 rounded-xl shadow-sm border-2 border-red-500/20">
                                        <h3 className="text-sm font-bold text-red-500 mb-3 uppercase tracking-wider flex items-center gap-2">
                                            <AlertTriangle className="w-4 h-4" />
                                            Reported {selectedReport.targetType}
                                        </h3>
                                        
                                        <div className="space-y-4">
                                            <div className="flex items-center gap-2">
                                                <span className="text-gray-500">Target ID:</span>
                                                <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-sm font-mono">
                                                    {selectedReport.reportedId}
                                                </code>
                                            </div>
                                            {selectedReport.reportedUsername && (
                                                 <div className="flex items-center gap-2">
                                                    <span className="text-gray-500">Username:</span>
                                                    <span className="font-bold text-black dark:text-white">@{selectedReport.reportedUsername?.replace(/^@/, '')}</span>
                                                </div>
                                            )}
                                            
                                            <div className="p-3 bg-gray-50 dark:bg-black/20 rounded-lg">
                                                <span className="text-xs font-bold text-gray-500 block mb-1">REASON</span>
                                                <p className="text-black dark:text-white">{selectedReport.reason}</p>
                                            </div>

                                            <div className="grid grid-cols-2 gap-4">
                                                <Button 
                                                    variant="outline" 
                                                    className="w-full"
                                                    onClick={() => {
                                                        // In a real app, this would navigate to the content
                                                        toast.info(`Inspect ${selectedReport.targetType} ${selectedReport.reportedId}`);
                                                        // Could dispatch navigation event here
                                                    }}
                                                >
                                                    <ExternalLink className="w-4 h-4 mr-2" />
                                                    Inspect Content
                                                </Button>
                                                <Button 
                                                    variant="outline" 
                                                    className="w-full text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                                    onClick={() => {
                                                        // Copy ID for Legion Action Panel
                                                        navigator.clipboard.writeText(selectedReport.reportedId);
                                                        toast.success("Target ID copied to clipboard");
                                                    }}
                                                >
                                                    <Shield className="w-4 h-4 mr-2" />
                                                    Copy ID for Action
                                                </Button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Resolution Controls */}
                                    <div className="bg-white dark:bg-[#1a1a1a] p-4 rounded-xl shadow-sm">
                                        <h3 className="text-sm font-bold text-gray-500 mb-3 uppercase tracking-wider">Resolution</h3>
                                        
                                        <textarea
                                            placeholder="Resolution notes (required if taking action)..."
                                            value={resolutionNote}
                                            onChange={(e) => setResolutionNote(e.target.value)}
                                            className="w-full p-3 rounded-lg bg-gray-50 dark:bg-black/20 border border-black/10 dark:border-white/10 min-h-[100px] mb-4"
                                        />

                                        <div className="flex gap-3">
                                            <Button 
                                                onClick={() => handleResolve('dismissed')}
                                                disabled={processing}
                                                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white"
                                            >
                                                <XCircle className="w-4 h-4 mr-2" />
                                                Dismiss Report
                                            </Button>
                                            <Button 
                                                onClick={() => handleResolve('resolved')}
                                                disabled={processing}
                                                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                                            >
                                                <CheckCircle className="w-4 h-4 mr-2" />
                                                Mark Resolved
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </ScrollArea>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8 text-center">
                            <Shield className="w-16 h-16 mb-4 opacity-20" />
                            <p className="text-lg font-medium">Select a report to review</p>
                            <p className="text-sm max-w-xs mt-2 opacity-60">
                                As a Watcher, you help maintain the safety and quality of the AfroYap community.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    </div>
    );
}
